import { User } from "../user/user.model";
import { Injectable } from "@angular/core";
import { FirebaseService } from "../firebase/firebase.service";
import { ProductFilterService } from "../product/product.filter";

import { ProductBuilder } from "../product/product.builder";
import { Product } from "../product/product.model";
import { Order } from "../order/order.model";
import { BehaviorSubject } from "rxjs";
import { CalendarEvent } from "../event/event.model";
import { CalendarEventBuilder } from "../event/event.builder";
import { Router } from "@angular/router";
import { EmailRecipient } from "../email/email.model";
import { UserBuilder } from "../user/user.builder";
import { EmailRecipientBuilder } from "../email/email.builder";
import { EmailRecipientFilterService } from "../email/email.filter";
import { environment } from "src/environments/environment";
import { UserFilterService } from "../user/user.filter";
import { AngularFirestore } from "@angular/fire/firestore";

import { AppState } from "./app.state";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { ToastService } from "ng-uikit-pro-standard";
import { AuthService } from "../services";
import { catchError, map } from "rxjs/operators";

@Injectable({
  providedIn: "root"
})
export class AppService {
  public state: AppState;

  constructor(
    public fb: FirebaseService,
    public db: AngularFirestore,
    public router: Router,
    public authService: AuthService,
    public httpClient: HttpClient,
    public toast: ToastService
  ) {
    this.getMaps();
    this.getFilters();
  }
  public async initializeApp(uid: string) {
    const userJWT = await this.authService.user.getIdToken();
    const httpHeaders = new HttpHeaders().set("Authorization", `${userJWT}`);
    let user: User = await this.httpClient
      .get<User>(`${environment.apiBaseUrl}/api/v1/users/${uid}`, {
        headers: httpHeaders
      })
      .pipe(
        map(user => new UserBuilder().load(user).build()),
        catchError(err => {
          throw err;
        })
      )
      .toPromise();
    this.state = new AppState(
      this.db,
      user,
      this.authService,
      this.httpClient,
      this.toast
    );
    await this.state.initializeAppState();
  }

  private _admins: Map<string, User>;
  private _recipients: Map<string, EmailRecipient>;

  public isBundleForm: boolean = false;

  private getMaps() {
    this._admins = new Map();
    this._recipients = new Map();
  }

  public onVenueChange: BehaviorSubject<boolean>;

  public userBuilder: UserBuilder;
  public productBuilder: ProductBuilder;
  public eventBuilder: CalendarEventBuilder;
  public emailRecipientBuilder: EmailRecipientBuilder;

  public filters: Map<
    string,
    ProductFilterService | UserFilterService | EmailRecipientFilterService
  >;

  get emailFilter(): EmailRecipientFilterService {
    return this.filters.get("emails") as EmailRecipientFilterService;
  }

  get adminFilter(): UserFilterService {
    return this.filters.get("admins") as UserFilterService;
  }

  //TODO: DEPRECATE FILTERS
  public getFilters() {
    this.filters = new Map();
    this.filters.set("emails", new EmailRecipientFilterService());
    this.filters.set("admins", new UserFilterService());
  }

  public async validateUser(email: string) {
    return this.httpClient
      .get<{ status: number }>(
        `${
          environment.apiBaseUrl
        }/api/v1/auth/validateUser/${email}`
      )
      .toPromise();
  }

  public getAdmins() {
    this._admins = new Map();
    this.state._users.getAdmins().subscribe(users => {
      this.adminFilter.next(users);
      users.map(user => {
        this._admins.set(user.id, user);
      });
    });
  }

  public async addUser() {
    const status = this.state._users.addUser(this.userBuilder.build());
    this.userBuilder = null;
    return status;
  }

  //TODO: MOVE INTO USER FACTORY
  public async updateUser(): Promise<void> {
    this.state._users.updateUser(this.userBuilder.build());
    this.userBuilder = null;
    return;
  }

  public async deleteUser() {
    this.state._users.deleteUser(this.userBuilder.build());
    this.userBuilder = null;
    return;
  }

  //TODO: MOVE INTO EMAIL FACTORY
  public getRecipients() {
    this._recipients = new Map();
    this.fb.emails$.subscribe(recipients => {
      this.emailFilter.next(recipients);
      recipients.map(recipient => {
        this._recipients.set(recipient.reference.id, recipient);
      });
    });
  }

  //TODO: MOVE INTO EMAIL FACTORY
  public get recipients(): EmailRecipient[] {
    return Array.from(this._recipients.values());
  }

  //TODO: MOVE INTO EMAIL FACTORY
  public async updateRecipient() {
    this.emailRecipientBuilder.build().update();
    return;
  }

  //TODO: MOVE INTO EMAIL FACTORY
  public async addRecipient() {
    await this.fb.db
      .collection(environment.recipients)
      .add(this.emailRecipientBuilder.build().toJSON());
  }

  //TODO: MOVE INTO EMAIL FACTORY
  public async deleteRecipient() {
    this.emailRecipientBuilder.build().delete();
    return;
  }

  //TODO: MOVE INTO USER FACTORY
  public addUserBuilder() {
    this.userBuilder = new UserBuilder();
  }

  //TODO: MOVE INTO USER FACTORY
  public editUserBuilder(user: User) {
    this.userBuilder = new UserBuilder().edit(user);
  }

  //TODO: MOVE INTO EMAIL FACTORY
  public addRecipientBuilder() {
    this.emailRecipientBuilder = new EmailRecipientBuilder();
  }

  //TODO: MOVE INTO EMAIL FACTORY
  public editRecipientBuilder(recipient: EmailRecipient) {
    this.emailRecipientBuilder = new EmailRecipientBuilder().edit(recipient);
  }

  //TODO: MOVE INTO EMAIL FACTORY
  public addEventBuilder() {
    this.eventBuilder = new CalendarEventBuilder();
  }

  public duplicateEventBuilder(event: CalendarEvent) {
    this.eventBuilder = new CalendarEventBuilder().duplicate(event);
  }

  public addCustomDateEventBuilder(date: Date) {
    this.eventBuilder = new CalendarEventBuilder();
    this.eventBuilder.setDate(date);
  }

  public editEventBuilder(event: CalendarEvent) {
    this.eventBuilder = new CalendarEventBuilder().edit(event);
  }

  public editProductBuilder(product: Product) {
    this.productBuilder = new ProductBuilder().edit(product);
  }

  public addProductBuilder() {
    this.productBuilder = new ProductBuilder().setVenue(this.state.venue.id);
  }

  public async updateProduct(product: Product) {
    this.isBundleForm = false;

    const httpHeaders = await this.state.getHTTPHeaders();
    await this.httpClient
      .put(
        `${environment.apiBaseUrl}/api/v1/products`,
        {
          id: product.id,
          venueId: this.state.venue.id,
          data: product.toAPIRequest()
        },
        {
          headers: httpHeaders
        }
      )
      .toPromise()
      .catch(error => {
        this.toast.error(`Could not update product: ${product.name}`, "Error", {
          opacity: 1
        });
        throw error;
      });
  }

  public async deleteEvent(productId: string, event: CalendarEvent) {
    const httpHeaders = await this.state.getHTTPHeaders();
    await this.httpClient
      .delete(
        `${environment.apiBaseUrl}/api/v1/products/${productId}/events/${event.id}`,
        {
          headers: httpHeaders
        }
      )
      .toPromise()
      .catch(error => {
        this.toast.error(
          `Could not delete event: ${event.shared.name}`,
          "Error",
          {
            opacity: 1
          }
        );
        throw error;
      });
    return;
  }

  redeem(order: Order) {
    this.fb.api.order.redeem(order);
  }

  unredeem(order: Order) {
    this.fb.api.order.unredeem(order);
  }
}
